<template>
  <header class="header" :class="{ scrolled: isScrolled }">
    <div class="header-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <img src="/logo-students.png" alt="Logo" class="studentlogo-image" />
      </div>

      <!-- Navigation Links -->
      <nav class="navigation">
        <router-link
          to="/student/find-tutors"
          class="nav-link"
          :class="{
            active:
              route.path === '/student/find-tutors' || route.name === 'book-session-with-tutor',
          }"
          >Find Tutors</router-link
        >
        <router-link
          to="/student/sessions"
          class="nav-link"
          :class="{
            active:
              route.path === '/student/sessions' ||
              route.path === '/student-session' ||
              route.path === '/student/study-materials' ||
              route.path === '/student/ai-generated-study-material' ||
              route.path.startsWith('/feedback'),
          }"
          >My Sessions</router-link
        >
        <span
          v-if="!canMessage"
          class="nav-link disabled"
          :title="'Your account must be verified before you can access messages'"
        >
          Messages
        </span>
        <router-link v-else to="/student-messages" class="nav-link messages-link" active-class="active">
          Messages
          <span
            v-if="hasUnreadBadge"
            class="unread-badge"
            :title="`${displayUnreadCount} conversation${displayUnreadCount === 1 ? '' : 's'} with unread messages`"
          >
            {{ displayUnreadCount }}
          </span>
        </router-link>
      </nav>

      <!-- Action Button / User Menu (Student) -->
      <div class="action-buttons">
        <div v-if="isLoggedIn" ref="menuRef" class="user-menu">
          <button class="user-button" type="button" @click="toggleUserMenu">
            <span class="user-name">{{ userDisplayName }}</span>

            <!-- Document Issue Indicator Badge -->
            <span
              v-if="studentStatus === 'document_issue'"
              class="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 border-2 border-white"
              title="Action required: Document resubmission needed"
            >
              <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>

            <svg
              class="dropdown-arrow"
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          <div v-show="isUserMenuOpen" class="user-dropdown">
            <router-link to="/student/profile" class="user-dropdown-link" @click="closeUserMenu">
              <svg class="w-4 h-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clip-rule="evenodd"
                />
              </svg>
              Profile
            </router-link>

            <div class="border-t border-gray-200"></div>

            <button class="user-dropdown-link" type="button" @click="handleChangePassword">
              <svg
                class="w-4 h-4 text-gray-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1 1 21 9z"
                />
              </svg>
              Change Password
            </button>

            <div class="border-t border-gray-200"></div>

            <button class="user-dropdown-link" type="button" @click="handleLogout">
              <svg
                class="w-4 h-4 text-red-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Log out
            </button>
          </div>
        </div>
        <!-- Show loading state when authentication is being verified -->
        <div v-else class="user-menu">
          <div class="user-button-loading">
            <span class="user-name">Loading...</span>
          </div>
        </div>
      </div>

      <!-- Mobile Hamburger Menu -->
      <div class="mobile-menu-container">
        <button class="mobile-menu-toggle" @click="toggleMobileMenu">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>

        <!-- Mobile Navigation Menu -->
        <div class="mobile-nav" :class="{ active: isMobileMenuOpen }">
          <div class="mobile-nav-content">
            <router-link to="/student/find-tutors" class="mobile-nav-link" @click="closeMobileMenu"
              >Find Tutors</router-link
            >
            <router-link to="/student/sessions" class="mobile-nav-link" @click="closeMobileMenu"
              >My Sessions</router-link
            >
            <span
              v-if="!canMessage"
              class="mobile-nav-link disabled"
              style="opacity: 0.5; cursor: not-allowed"
            >
              Messages
            </span>
            <router-link
              v-else
              to="/student-messages"
              class="mobile-nav-link messages-link"
              @click="closeMobileMenu"
            >
              Messages
              <span
                v-if="hasUnreadBadge"
                class="unread-badge mobile"
                :title="`${displayUnreadCount} conversation${displayUnreadCount === 1 ? '' : 's'} with unread messages`"
              >
                {{ displayUnreadCount }}
              </span>
            </router-link>
            <div class="border-t border-white/20 my-2"></div>
            <router-link to="/student/profile" class="mobile-nav-link" @click="closeMobileMenu"
              >Profile</router-link
            >
            <button class="mobile-nav-link" type="button" @click="handleChangePassword">
              Change Password
            </button>
            <button class="mobile-nav-link" type="button" @click="handleLogout">Log out</button>
          </div>
        </div>
      </div>
    </div>
  </header>

  <!-- Toast Container -->
  <ToastContainer />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { auth, db, onAuthStateChanged, sendPasswordResetEmail, doc, getDoc } from "@/firebase";
import { signOut } from "firebase/auth";
import { useToast } from "@/composables/useToast";
import ToastContainer from "@/components/ToastContainer.vue";
import { useStudentStatus } from "@/composables/useStudentStatus";
import { useMessagingStore } from "@/stores/messagingStore";
import { useMessagingSync } from "@/composables/useMessagingSync";
import { messagingPermissionService } from "@/services/messagingPermissionService";
import { ensureInitializedAndLoggedIn, fetchUnreadMessageCountMap, subscribeToUnreadMessages, logoutCometChat } from "@/services/cometchat";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";
import "@/assets/main.css";

// Router
const router = useRouter();
const route = useRoute();

// Toast notifications
const { showSuccess, showError } = useToast();

// Student status composable
const { canMessage, status: studentStatus } = useStudentStatus();

// Messaging store (replaces useUnreadMessages composable)
const messagingStore = useMessagingStore();
const { broadcastUnreadCountsUpdate, broadcastConversationRead } = useMessagingSync('student');

// Computed properties from store
const displayUnreadCount = computed(() => messagingStore.unreadConversationCount);
const hasUnreadBadge = computed(() => messagingStore.hasUnreadBadge);

// Watch for changes in unread count
watch(() => messagingStore.unreadConversationCount, (newVal, oldVal) => {
  logInfo('🔔 Total unread conversations changed in student header', {
    oldValue: oldVal,
    newValue: newVal,
    displayCount: displayUnreadCount.value,
    hasUnreadBadge: hasUnreadBadge.value
  });
});

// Track allowed contact IDs (only badge counts for these)
const contactIds = ref<Set<string>>(new Set());

const syncContactIds = async (userId: string) => {
  try {
    const contacts = await messagingPermissionService.getStudentMessagingContacts(userId);
    contactIds.value = new Set(contacts.map((c: any) => String(c.id || '').toLowerCase()).filter(Boolean));
    logDebug('Synced messaging contact IDs for badge filtering', { count: contactIds.value.size });
    return contactIds.value;
  } catch (error) {
    logWarn('Failed to load contact IDs for unread filtering', { error });
    return contactIds.value;
  }
};

// Load explicitly read conversations from localStorage (shared with StudentMessagesView)
const EXPLICITLY_READ_KEY = 'elevateED_explicitly_read_student';
const getExplicitlyReadConversations = (): Set<string> => {
  try {
    const stored = localStorage.getItem(EXPLICITLY_READ_KEY);
    const conversations = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    logDebug('📦 Loaded explicitly read conversations in header', {
      count: conversations.size,
      conversations: Array.from(conversations).slice(0, 5).map(id => id.substring(0, 8) + '***')
    });
    return conversations;
  } catch (error) {
    logWarn('Failed to load explicitly read conversations in header', { error });
    return new Set<string>();
  }
};

const filterToContacts = (unreadMap: Record<string, number>) => {
  if (!unreadMap) return {};
  if (!contactIds.value.size) return unreadMap; // if we don't know contacts yet, don't drop counts
  const currentUserId = auth.currentUser?.uid?.toLowerCase();
  const explicitlyRead = getExplicitlyReadConversations();
  const filtered: Record<string, number> = {};
  
  // Load recently marked read timestamps to avoid race conditions
  let recentlyMarkedRead: Record<string, number> = {};
  try {
    const stored = localStorage.getItem('elevateED_recently_marked_read_student');
    if (stored) {
      recentlyMarkedRead = JSON.parse(stored);
    }
  } catch {}
  
  logDebug('🔍 Filtering unread map in header', {
    totalUnread: Object.keys(unreadMap).length,
    explicitlyReadCount: explicitlyRead.size,
    explicitlyReadIds: Array.from(explicitlyRead).slice(0, 5).map(id => id.substring(0, 8) + '***'),
    unreadMapIds: Object.keys(unreadMap).slice(0, 5).map(id => id.substring(0, 8) + '***')
  });
  
  const now = Date.now();
  
  Object.entries(unreadMap).forEach(([id, count]) => {
    const key = String(id || '').toLowerCase();
    
    // Check if this was explicitly marked as read
    const wasExplicitlyRead = explicitlyRead.has(key);
    
    // Check if this was recently marked as read (within last 60 seconds)
    // This gives CometChat plenty of time to process the "mark as read" request
    const recentlyMarked = recentlyMarkedRead[key] && (now - recentlyMarkedRead[key] < 60000);
    
    // DON'T remove from explicitly read set just because CometChat shows unread.
    // Only remove when a REAL new message arrives (handled by handleIncomingUnread listener).
    // The recentlyMarked check provides extra protection during CometChat's sync delay.
    if (wasExplicitlyRead && recentlyMarked) {
      logDebug('⏳ Protecting recently marked conversation from removal', {
        conversationId: id.substring(0, 8) + '***',
        ageMs: now - recentlyMarkedRead[key]
      });
    }
    
    // Skip self-conversations, non-contacts, and explicitly read conversations
    if (key !== currentUserId && contactIds.value.has(key) && count > 0 && !explicitlyRead.has(key)) {
      filtered[id] = count;
    } else if (explicitlyRead.has(key) && count > 0) {
      logDebug('✅ Filtering out explicitly read conversation from badge', {
        conversationId: id.substring(0, 8) + '***',
        cometchatCount: count,
        reason: 'explicitly read by user'
      });
    } else if (key === currentUserId) {
      logDebug('Filtering out self-conversation', {
        conversationId: id.substring(0, 8) + '***'
      });
    } else if (!contactIds.value.has(key)) {
      logDebug('Filtering out non-contact', {
        conversationId: id.substring(0, 8) + '***'
      });
    }
  });
  
  // NOTE: We removed the code that deletes conversations from explicitlyRead
  // based on CometChat counts. Only handleIncomingUnread (real-time listener)
  // should remove conversations from the explicitly read set.
  
  logInfo('🎯 Final filtered unread counts in header', {
    beforeFilter: Object.keys(unreadMap).length,
    afterFilter: Object.keys(filtered).length,
    filteredIds: Object.keys(filtered).slice(0, 5).map(id => id.substring(0, 8) + '***')
  });
  
  return filtered;
};

// Clean up old "recently marked read" timestamps (older than 60 seconds)
const cleanupRecentlyMarkedRead = () => {
  try {
    const stored = localStorage.getItem('elevateED_recently_marked_read_student');
    if (!stored) return;
    
    const recentlyMarkedRead = JSON.parse(stored);
    const now = Date.now();
    let cleaned = false;
    
    for (const [key, timestamp] of Object.entries(recentlyMarkedRead)) {
      if (now - (timestamp as number) > 60000) {
        delete recentlyMarkedRead[key];
        cleaned = true;
      }
    }
    
    if (cleaned) {
      localStorage.setItem('elevateED_recently_marked_read_student', JSON.stringify(recentlyMarkedRead));
      logDebug('🧹 Cleaned up old recently marked read timestamps', {
        remaining: Object.keys(recentlyMarkedRead).length
      });
    }
  } catch (error) {
    logWarn('Failed to cleanup recently marked read timestamps', { error });
  }
};

const handleIncomingUnread = (userId: string, senderUid: string) => {
  void (async () => {
    try {
      const normalizedSender = String(senderUid || '').toLowerCase();
      const normalizedUserId = String(userId || '').toLowerCase();
      
      // Skip self-conversations (student messaging themselves)
      if (normalizedSender === normalizedUserId) {
        logDebug('Ignoring unread increment for self-conversation', { senderUid });
        return;
      }
      
      if (!contactIds.value.size) {
        await syncContactIds(userId);
      }
      
      if (!contactIds.value.has(normalizedSender)) {
        logDebug('Ignoring unread increment for non-contact sender', { senderUid });
        return;
      }
      
      // If this conversation was explicitly marked as read, remove it from the set
      // because there are NEW messages now
      // IMPORTANT: Always reload from localStorage to avoid overwriting changes from other components
      const explicitlyRead = getExplicitlyReadConversations();
      if (explicitlyRead.has(normalizedSender)) {
        explicitlyRead.delete(normalizedSender);
        try {
          localStorage.setItem(EXPLICITLY_READ_KEY, JSON.stringify([...explicitlyRead]));
          logInfo('🆕 Removed conversation from explicitly read due to NEW incoming message', {
            senderId: senderUid.substring(0, 8) + '***',
            remainingCount: explicitlyRead.size
          });
        } catch (error) {
          logWarn('Failed to update explicitly read conversations', { error });
        }
      }
      
      // Immediately increment the unread count in the store
      // This ensures the badge updates instantly when a message arrives
      const timestamp = Date.now();
      messagingStore.handleNewMessage(normalizedSender, '[New Message]', timestamp, normalizedSender);
      
      // Broadcast to other tabs
      broadcastUnreadCountsUpdate(Object.fromEntries(messagingStore.unreadCounts));
      
      logInfo('📨 Badge updated for incoming message in header', {
        senderId: senderUid.substring(0, 8) + '***',
        newBadgeCount: messagingStore.unreadConversationCount
      });
    } catch (error) {
      logWarn('Failed processing incoming unread for badge', { error, senderUid });
    }
  })();
};

const refreshUnreadFromCometChat = async () => {
  try {
    await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
    if (auth.currentUser?.uid && !contactIds.value.size) {
      await syncContactIds(auth.currentUser.uid);
    }
    
    // Get fresh counts from CometChat
    const rawUnreadMap = await fetchUnreadMessageCountMap();
    
    // Filter to only contacts, respecting explicitly read set
    const unreadMap = filterToContacts(rawUnreadMap);
    
    // Update store with filtered counts
    messagingStore.setUnreadCounts(unreadMap);
    
    // Broadcast to other tabs
    broadcastUnreadCountsUpdate(unreadMap);
    
    logInfo('Synced unread counts from CometChat', { 
      conversationsWithUnread: Object.keys(unreadMap).length,
      conversationIds: Object.keys(unreadMap).slice(0, 20)
    });
    
    return unreadMap;
  } catch (error) {
    logWarn('Failed to sync unread counts from CometChat in header', { error });
    return null;
  }
};

// Dev helper to inspect unread map on demand
if (typeof window !== 'undefined') {
  (window as any).debugUnreadCounts = async () => {
    try {
      const unreadMap = await refreshUnreadFromCometChat();
      return {
        message: 'Unread sync triggered',
        conversationsWithUnread: unreadMap ? Object.keys(unreadMap).length : 0,
        conversationIds: unreadMap ? Object.keys(unreadMap) : [],
        unreadMap,
      };
    } catch (error) {
      logWarn('debugUnreadCounts failed', { error });
      return { message: 'Failed to sync unread counts', error };
    }
  };

  // Helper to inspect contacts vs unread map (raw and filtered)
  (window as any).debugContactsAndUnread = async () => {
    try {
      await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
      const contacts = auth.currentUser
        ? await messagingPermissionService.getStudentMessagingContacts(auth.currentUser.uid)
        : [];
      const contactIdsArr = contacts.map((c: any) => c.id);
      const rawUnread = await fetchUnreadMessageCountMap();
      const filteredUnread = filterToContacts(rawUnread);
      return {
        contactsCount: contactIdsArr.length,
        contactIds: contactIdsArr,
        rawUnreadCount: Object.keys(rawUnread || {}).length,
        rawUnread,
        filteredUnreadCount: Object.keys(filteredUnread || {}).length,
        filteredUnread,
      };
    } catch (error) {
      logWarn('debugContactsAndUnread failed', { error });
      return { message: 'Failed to load contacts/unread', error };
    }
  };

  // Helper to clear unread for a specific ghost conversation
  (window as any).clearGhostConversation = async (conversationId: string) => {
    try {
      await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
      logInfo('Clearing ghost conversation', { conversationId });
      
      // Read receipts disabled - not marking messages as read in CometChat
      logDebug('Read receipts disabled - ghost conversation cleared locally only', {
        conversationId: conversationId.substring(0, 8) + '***'
      });
      
      // Force-clear this conversation from local unread state
      messagingStore.markAsRead(conversationId.toLowerCase());
      
      // Re-sync unread counts after clearing
      await refreshUnreadFromCometChat();
      
      return {
        success: true,
        conversationId,
        messagesMarkedAsRead: 0,
        message: `Cleared ghost conversation (local only - read receipts disabled)`
      };
    } catch (error) {
      logError('Failed to clear ghost conversation', { conversationId, error });
      // Even if CometChat fails, clear it locally
      messagingStore.markAsRead(conversationId.toLowerCase());
      return {
        success: true,
        conversationId,
        clearedLocally: true,
        error: error instanceof Error ? error.message : String(error),
        message: 'Cleared ghost conversation locally (CometChat operation failed)'
      };
    }
  };
}


// Mobile menu state
const isMobileMenuOpen = ref(false);

// User menu state
const isUserMenuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

// Auth/user state
const firebaseUser = ref<any | null>(null);
const storedUser = ref<any | null>(null);
const userData = ref<any | null>(null);

// Persistent display name that doesn't reset during navigation
const persistentDisplayName = ref<string>(getInitialDisplayName());

function looksLikeUID(str: string): boolean {
  return !!(str && str.length > 20 && /^[a-zA-Z0-9]+$/.test(str));
}

// Cache for display name to persist across navigation
const DISPLAY_NAME_CACHE_KEY = "elevateED_user_display_name";
const USER_DATA_CACHE_KEY = "elevateED_user_data_cache";

// Function to get cached user data
function getCachedUserData() {
  try {
    const cached = sessionStorage.getItem(USER_DATA_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      // Cache valid for 15 minutes
      if (age < 15 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {
    console.warn("Cache read error:", e);
  }
  return null;
}

// Function to cache user data
function cacheUserData(data: any) {
  try {
    sessionStorage.setItem(
      USER_DATA_CACHE_KEY,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    console.warn("Cache write error:", e);
  }
}

// Function to get initial display name from existing data
function getInitialDisplayName(): string {
  // First check sessionStorage for cached display name (persists across navigation)
  try {
    const cachedName = sessionStorage.getItem(DISPLAY_NAME_CACHE_KEY);
    if (cachedName && cachedName !== "null" && cachedName !== "undefined") {
      const name = JSON.parse(cachedName);
      if (name && !looksLikeUID(name) && !name.includes("@") && name !== "Loading...") {
        return name;
      }
    }
  } catch {}

  // Check cached user data
  const cachedUserData = getCachedUserData();
  if (cachedUserData) {
    if (cachedUserData.firstName && cachedUserData.lastName) {
      const name = `${cachedUserData.firstName} ${cachedUserData.lastName}`;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
      return name;
    }
  }

  // Check localStorage for existing user data
  try {
    const storedUserData = JSON.parse(localStorage.getItem("user") || "null");
    if (storedUserData) {
      if (storedUserData.firstName && storedUserData.lastName) {
        const name = `${storedUserData.firstName} ${storedUserData.lastName}`;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
        return name;
      }
      // Check username/name but skip if it looks like a UID or email
      const fallbackName = storedUserData.username || storedUserData.name;
      if (fallbackName && !looksLikeUID(fallbackName) && !fallbackName.includes("@")) {
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(fallbackName));
        return fallbackName;
      }
    }
  } catch {}

  // Check if we have auth token indicating logged-in state
  const hasToken = !!localStorage.getItem("token");
  const hasUser = !!localStorage.getItem("user");

  // If we have auth indicators but no name yet, show a placeholder that's not "Guest"
  if (hasToken || hasUser) {
    return "Loading...";
  }

  // Only return "Guest" if we're truly not logged in
  return "Guest";
}

// Scroll state
const isScrolled = ref(false);

// Add scroll event listener
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

// Check if current route is login-related
const isLoginRoute = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes("/login") || path.includes("login");
});

// Is authenticated
const isLoggedIn = computed(() => {
  // Check if we have actual user data, not just a token
  return (
    !!firebaseUser.value ||
    !!storedUser.value ||
    (!!localStorage.getItem("token") && !!localStorage.getItem("user"))
  );
});

// Function to update display name only when we have valid name data
const updateDisplayName = () => {
  // Don't update if we already have a valid cached name
  if (
    persistentDisplayName.value &&
    persistentDisplayName.value !== "Guest" &&
    persistentDisplayName.value !== "Loading..."
  ) {
    return;
  }

  // First check if we have Firebase user data with first and last name
  if (userData.value && userData.value.firstName && userData.value.lastName) {
    const name = `${userData.value.firstName} ${userData.value.lastName}`;
    persistentDisplayName.value = name;
    sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
    return;
  }

  // Check stored user data for name fields
  if (storedUser.value) {
    if (storedUser.value.firstName && storedUser.value.lastName) {
      const name = `${storedUser.value.firstName} ${storedUser.value.lastName}`;
      persistentDisplayName.value = name;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
      return;
    }
    const fallbackName = storedUser.value.username || storedUser.value.name;
    if (fallbackName && !looksLikeUID(fallbackName) && !fallbackName.includes("@")) {
      persistentDisplayName.value = fallbackName;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(fallbackName));
      return;
    }
  }

  // Check localStorage for user data with name fields
  try {
    const parsed = JSON.parse(localStorage.getItem("user") || "null");
    if (parsed) {
      if (parsed.firstName && parsed.lastName) {
        const name = `${parsed.firstName} ${parsed.lastName}`;
        persistentDisplayName.value = name;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
        return;
      }
      const fallbackName = parsed.username || parsed.name;
      if (fallbackName && !looksLikeUID(fallbackName) && !fallbackName.includes("@")) {
        persistentDisplayName.value = fallbackName;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(fallbackName));
        return;
      }
    }
  } catch {}

  // Only use Firebase display name if it's not an email, UID, or gibberish
  if (firebaseUser.value) {
    const displayName = firebaseUser.value.displayName;
    if (displayName && !displayName.includes("@") && !looksLikeUID(displayName)) {
      persistentDisplayName.value = displayName;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(displayName));
      return;
    }
  }

  // Only set to Guest if user is not logged in (don't change from Loading if still logged in)
  if (!isLoggedIn.value) {
    persistentDisplayName.value = "Guest";
    sessionStorage.removeItem(DISPLAY_NAME_CACHE_KEY);
  }
  // If logged in but no name data yet, keep existing value (could be "Loading...")
};

// Derive display name - just return the persistent value
const userDisplayName = computed(() => {
  return persistentDisplayName.value;
});

// Navigation functions

const navigateToLogin = () => {
  router.push({ name: "login" });
  closeMobileMenu();
};

// User menu handlers
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node;

  // Close user menu if clicking outside
  if (menuRef.value && !menuRef.value.contains(target)) {
    isUserMenuOpen.value = false;
  }
};

const handleLogout = async () => {
  // Immediately set to loading state to prevent "Guest" flash
  persistentDisplayName.value = "Logging out...";

  try {
    // Cleanly logout CometChat to avoid stale session on next login
    await logoutCometChat();
    await signOut(auth);
  } catch (error) {
    logError("Firebase signout error", { error });
  }

  // Clear all localStorage data
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("authToken");
  localStorage.removeItem("userRole");
  localStorage.clear();

  // Clear sessionStorage cache
  sessionStorage.removeItem(DISPLAY_NAME_CACHE_KEY);
  sessionStorage.removeItem(USER_DATA_CACHE_KEY);

  // Clear user state
  firebaseUser.value = null;
  storedUser.value = null;
  userData.value = null;
  
  // Reset messaging store
  messagingStore.reset();

  if (unsubscribeUnreadListener) {
    unsubscribeUnreadListener();
    unsubscribeUnreadListener = null;
  }

  closeUserMenu();

  // Use router.replace() to prevent back button navigation to dashboard
  // This also clears the navigation history
  router.replace({ name: "login" });
};

const handleChangePassword = async () => {
  closeUserMenu();

  // Get user email from Firebase user or stored user
  let userEmail = "";
  if (firebaseUser.value) {
    userEmail = firebaseUser.value.email;
  } else if (storedUser.value) {
    userEmail = storedUser.value.email;
  } else {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      if (parsed) {
        userEmail = parsed.email;
      }
    } catch {}
  }

  if (!userEmail) {
    showError("Unable to find user email. Please log out and log back in.");
    return;
  }

  try {
    // Get the current domain for the action URL
    const actionUrl = `${window.location.origin}/password-reset`;

    await sendPasswordResetEmail(auth, userEmail, {
      url: actionUrl,
      handleCodeInApp: false,
    });

    showSuccess(
      "Password reset email sent successfully! Check your inbox and spam folder for the reset link."
    );
  } catch (error: any) {
    console.error("Password reset error:", error);

    if (error.code === "auth/user-not-found") {
      showError("No account found with this email address.");
    } else if (error.code === "auth/invalid-email") {
      showError("Invalid email address.");
    } else if (error.code === "auth/too-many-requests") {
      showError("Too many requests. Please try again later.");
    } else {
      showError("Failed to send reset email. Please try again.");
    }
  }
};

// Fetch user data from Firebase users collection
const fetchUserData = async (userId: string) => {
  try {
    // Check cache first
    const cachedData = getCachedUserData();
    if (cachedData && cachedData.uid === userId) {
      userData.value = cachedData;
      updateDisplayName();
      return;
    }

    // Fetch from Firestore
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      userData.value = { ...data, uid: userId };

      // Cache the user data
      cacheUserData(userData.value);

      // Update display name when user data is fetched
      updateDisplayName();
    }
  } catch (error) {
    logError("Error fetching user data for header", { error });
  }
};

// Track initialization state
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let unsubscribeUnreadListener: (() => void) | null = null;

const getCometChatDisplayName = () => {
  const nameCandidates = [
    userDisplayName.value,
    `${userData.value?.firstName || ""} ${userData.value?.lastName || ""}`.trim(),
    storedUser.value?.name,
    storedUser.value?.username,
  ].filter(Boolean) as string[];
  const name = nameCandidates.find(n => n && n !== "Guest" && n !== "Loading...");
  return name || "User";
};

// Initialize unread message counts for the badge
const initializeUnreadMessageCounts = async (userId: string, force = false) => {
  // Prevent multiple simultaneous initializations unless forced
  if (!force && (isInitialized || initializationPromise)) {
    if (initializationPromise) {
      await initializationPromise;
    }
    return;
  }

  initializationPromise = (async () => {
    try {
      logDebug('Initializing messaging store for student', { userId: userId.substring(0, 8) + '***', force });
      
      // Initialize the messaging store
      await messagingStore.initialize(userId, 'student');
      
      // Ensure CometChat session is ready
      await ensureInitializedAndLoggedIn(userId, getCometChatDisplayName());

      // Sync contact IDs before fetching unread counts
      if (!contactIds.value.size) {
        await syncContactIds(userId);
      }

      // Fetch and filter unread counts from CometChat
      const rawUnreadMap = await fetchUnreadMessageCountMap();
      const filteredUnreadMap = filterToContacts(rawUnreadMap);
      
      // Update store
      messagingStore.setUnreadCounts(filteredUnreadMap);
      
      // Broadcast to other tabs
      broadcastUnreadCountsUpdate(filteredUnreadMap);
      
      isInitialized = true;
      
      logInfo('Messaging store initialized for student', { 
        userId: userId.substring(0, 8) + '***',
        conversationsWithUnread: Object.keys(filteredUnreadMap).length,
      });
    } catch (error) {
      logError('Failed to initialize messaging store for student', {
        userId: userId.substring(0, 8) + '***',
        error
      });
    } finally {
      initializationPromise = null;
    }
  })();

  await initializationPromise;
};

// Debounce state for refresh
let refreshTimeout: number | null = null;
let isRefreshing = false;

// Refresh unread message counts periodically
const refreshUnreadMessageCounts = async (userId: string) => {
  // Prevent multiple simultaneous refresh calls
  if (isRefreshing) {
    logDebug('Refresh already in progress, skipping', { userId: userId.substring(0, 8) + '***' });
    return;
  }
  
  // Clear any pending refresh
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  
  // Debounce the refresh call
  refreshTimeout = setTimeout(async () => {
    try {
      isRefreshing = true;
      
      // Try to refresh via CometChat unread map
      try {
        await ensureInitializedAndLoggedIn(userId, getCometChatDisplayName());
        if (auth.currentUser?.uid && !contactIds.value.size) {
          await syncContactIds(auth.currentUser.uid);
        }
        const unreadMap = filterToContacts(await fetchUnreadMessageCountMap());
        messagingStore.setUnreadCounts(unreadMap);
        broadcastUnreadCountsUpdate(unreadMap);
        logDebug('Refreshed unread message counts for student via CometChat', { 
          userId: userId.substring(0, 8) + '***',
          conversationsWithUnread: Object.keys(unreadMap).length
        });
      } catch (cometchatError) {
        logWarn('CometChat refresh failed', { cometchatError });
      }
    } catch (error) {
      logDebug('Failed to refresh unread message counts for student', {
        userId: userId.substring(0, 8) + '***',
        error
      });
    } finally {
      isRefreshing = false;
      refreshTimeout = null;
    }
  }, 1000); // 1 second debounce
};

// Page visibility listener to refresh when tab becomes active
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && auth.currentUser) {
    logDebug('Page became visible, refreshing unread counts');
    initializeUnreadMessageCounts(auth.currentUser.uid, true); // Force refresh
  }
};

// Init
onMounted(() => {
  // Load stored user from localStorage (set during login)
  try {
    storedUser.value = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser.value = null;
  }

  // Update display name initially
  updateDisplayName();

  // Listen to Firebase auth changes
  onAuthStateChanged(auth, async (user) => {
    firebaseUser.value = user;
    if (user) {
      // Fetch additional user data from Firestore
      await fetchUserData(user.uid);
    } else {
      // Clear user data and keep storedUser in case backend login info exists
      userData.value = null;
      try {
        storedUser.value = JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        storedUser.value = null;
      }
    }
    // Update display name after auth state change
    updateDisplayName();
    
    // Initialize unread message counts when user is authenticated
    if (user) {
      try {
        await initializeUnreadMessageCounts(user.uid);
        
        // Set up CometChat unread listener AFTER initialization succeeds
        // This ensures CometChat is logged in before we register the listener
        const listenerId = `student-header-unread-${user.uid}`;
        if (unsubscribeUnreadListener) {
          unsubscribeUnreadListener();
        }
        unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, user.uid, (senderUid) => {
          handleIncomingUnread(user.uid, senderUid);
        });
        
        logInfo('✅ CometChat message listener registered in student header', {
          listenerId,
          userId: user.uid.substring(0, 8) + '***'
        });
      } catch (error) {
        logError('❌ Failed to initialize messaging in student header', { error });
        
        // Even if initialization fails, try to set up the listener
        // It might work if CometChat was already initialized
        try {
          const listenerId = `student-header-unread-${user.uid}`;
          if (unsubscribeUnreadListener) {
            unsubscribeUnreadListener();
          }
          unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, user.uid, (senderUid) => {
            handleIncomingUnread(user.uid, senderUid);
          });
          logInfo('⚠️ Listener registered despite init error', { listenerId });
        } catch (listenerError) {
          logError('❌ Failed to register message listener', { listenerError });
        }
      }
      
      // Set up periodic refresh of unread counts (every 30 seconds)
      const refreshInterval = setInterval(() => {
        if (auth.currentUser) {
          refreshUnreadMessageCounts(auth.currentUser.uid);
          // Also cleanup old timestamps
          cleanupRecentlyMarkedRead();
        }
      }, 30000);
      
      // Store interval for cleanup
      (window as any).__unreadRefreshInterval = refreshInterval;
      
      // Set up periodic check to ensure listener is still registered (every 60 seconds)
      // This helps recover from any CometChat connection issues
      const listenerCheckInterval = setInterval(() => {
        if (auth.currentUser && !unsubscribeUnreadListener) {
          logWarn('⚠️ Message listener lost, re-registering...', {
            userId: auth.currentUser.uid.substring(0, 8) + '***'
          });
          
          const listenerId = `student-header-unread-${auth.currentUser.uid}`;
          const currentUid = auth.currentUser.uid;
          
          unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, currentUid, (senderUid) => {
            handleIncomingUnread(currentUid, senderUid);
          });
          
          logInfo('✅ Message listener re-registered', { listenerId });
        }
      }, 60000);
      
      // Store interval for cleanup
      (window as any).__listenerCheckInterval = listenerCheckInterval;
      
      // Listen for unread state changes from MessagesView to keep header in sync
      const handleUnreadStateChange = (event: CustomEvent) => {
        const { conversationId, action } = event.detail || {};
        logInfo('🔔 Header received unread-state-changed event', {
          conversationId: conversationId?.substring(0, 8) + '***',
          action
        });
        
        if (action === 'marked-read' && conversationId) {
          // Mark as read in the store
          void messagingStore.markAsRead(conversationId);
          broadcastConversationRead(conversationId);
        }
      };
      
      window.addEventListener('unread-state-changed', handleUnreadStateChange as EventListener);
      
      // Store for cleanup
      (window as any).__unreadStateChangeListener = handleUnreadStateChange;
    }
  });

  // Additional initialization on page load (in case auth state change missed)
  if (auth.currentUser) {
    const currentAuthUser = auth.currentUser; // Capture to prevent null access after async operations
    (async () => {
      try {
        await initializeUnreadMessageCounts(currentAuthUser.uid);
        await refreshUnreadFromCometChat();
      } catch (error) {
        logWarn('Initial unread init on mount failed for student', { error });
      }
      // Check if still authenticated after async operations
      if (!auth.currentUser) {
        logWarn('User logged out during unread initialization');
        return;
      }
      const listenerId = `student-header-unread-${currentAuthUser.uid}`;
      if (unsubscribeUnreadListener) {
        unsubscribeUnreadListener();
      }
      unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, currentAuthUser.uid, (senderUid) => {
        handleIncomingUnread(currentAuthUser.uid, senderUid);
      });
    })();
  }

  document.addEventListener("click", onDocumentClick);
  window.addEventListener("scroll", handleScroll, { passive: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Close mobile menu when switching to desktop view
  const handleResize = () => {
    if (window.innerWidth > 768 && isMobileMenuOpen.value) {
      isMobileMenuOpen.value = false;
    }
  };
  window.addEventListener("resize", handleResize);

  // Store resize handler for cleanup
  (window as any).__headerResizeHandler = handleResize;
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("visibilitychange", handleVisibilityChange);

  // Remove resize handler
  const resizeHandler = (window as any).__headerResizeHandler;
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
    delete (window as any).__headerResizeHandler;
  }
  
  // Clean up unread refresh interval
  if ((window as any).__unreadRefreshInterval) {
    clearInterval((window as any).__unreadRefreshInterval);
    delete (window as any).__unreadRefreshInterval;
  }
  
  // Clean up listener check interval
  if ((window as any).__listenerCheckInterval) {
    clearInterval((window as any).__listenerCheckInterval);
    delete (window as any).__listenerCheckInterval;
  }
  
  // Clean up unread state change listener
  const unreadStateListener = (window as any).__unreadStateChangeListener;
  if (unreadStateListener) {
    window.removeEventListener('unread-state-changed', unreadStateListener);
    delete (window as any).__unreadStateChangeListener;
  }

  // Remove CometChat unread listener
  if (unsubscribeUnreadListener) {
    unsubscribeUnreadListener();
    unsubscribeUnreadListener = null;
  }
  
  // Clean up refresh timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
});

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
/* Add padding to body to account for fixed header */
body {
  padding-top: 120px;
}

.header {
  width: 100%;
  padding: 16px 24px;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
}

.header-container {
  display: flex;
  align-items: center;
  max-width: 80em;
  width: 90%;
  height: 6em;
  margin: 0 auto;
  margin-top: 1.25em;
  background: #5271ff !important;
  border-radius: 50px;
  padding: 12px 24px;
  border: 3px solid #000;
  box-shadow: 8px 8px 0px #000;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* Blur Effect for Header - Always active for better performance */
.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1);
  z-index: -1;
}

/* Optional: Add subtle opacity change on scroll for visual feedback */
.header.scrolled::before {
  background-color: rgba(255, 255, 255, 0.15);
}

/* Logo Section */
.logo-section {
  height: auto;
  width: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.studentlogo-image {
  height: 50px !important;
  padding-top: 5px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.logo-text {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
}

/* Navigation */
.navigation {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-left: auto;
  margin-right: 24px;
}

.nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  text-decoration: none;
  padding: 8px 0;
  position: relative;
  transition: all 0.2s ease;
}

.nav-link:hover {
  color: #ffffff;
  font-weight: 700;
  background: none !important;
}

.nav-link:hover::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #ffffff !important;
  border-radius: 2px;
}

.nav-link.active {
  font-weight: 700;
  color: #ffffff;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 5px;
  background: #ffffff !important;
  border-radius: 10px;
}

/* Ensure no background colors on any nav-link states */
.nav-link,
.nav-link:hover,
.nav-link:focus,
.nav-link:active,
.nav-link.active {
  background: transparent !important;
  background-color: transparent !important;
}

/* Mobile Hamburger Menu */
.mobile-menu-container {
  position: relative;
  display: none;
}

.mobile-menu-toggle {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 25px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background: #ffffff;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Mobile Navigation */
.mobile-nav {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  background: #5271ff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}

.mobile-nav.active {
  display: block;
}

.mobile-nav-content {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.mobile-nav-link {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  text-decoration: none;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  background: transparent;
  border-left: none;
  border-right: none;
  border-top: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 0;
  margin-bottom: 0;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active {
  color: #ffffff;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.1);
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.mobile-nav-link:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.mobile-nav-link.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn {
  padding: 10px 35px;
  border-radius: 24px;
  font-size: 18px;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.btn-login {
  background: #ffffff;
  color: rgb(0, 0, 0);
  border: 3px solid #000;
  width: 10em;
}

.btn-login:hover,
.btn-login:active,
.btn-login.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

.btn-register {
  background: white;
  color: #000;
  border: 3px solid #000;
}

.btn-register:hover,
.btn-register:active,
.btn-register.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

/* User menu */
.user-menu {
  position: relative;
}

.user-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border: 3px solid #000;
  color: #000;
  background: #fff;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 4px 4px 0px #000;
  min-width: 180px;
  justify-content: space-between;
  position: relative;
}

.user-button:hover {
  background: #fcbb02;
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0px #000;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #5271ff;
  border-radius: 50%;
  color: #fff;
}

.user-icon {
  width: 20px;
  height: 20px;
}

.user-name {
  flex: 1;
  text-align: left;
  font-weight: 600;
  color: #000;
}

.dropdown-arrow {
  color: #666;
  transition: transform 0.2s ease;
}

.user-button:hover .dropdown-arrow {
  color: #000;
}

.user-button-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
  opacity: 0.7;
}

.user-dropdown {
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 200px; /* Increased width to accommodate "Change Password" */
  background: #ffffff;
  border: 1px solid rgba(25, 20, 0, 0.21);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}
.user-dropdown-link {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
  background: #ffffff;
  border: none;
  cursor: pointer;
  text-decoration: none;
}
.user-dropdown-link:hover {
  background: #f9fafb;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-container {
    max-width: 90%;
    width: 90%;
  }
}

@media (max-width: 900px) {
  .header-container {
    max-width: 92%;
    width: 92%;
    margin-top: 0.8em;
  }
}

@media (max-width: 900px) {
  .header-container {
    width: calc(100% - 32px);
    max-width: calc(100vw - 32px);
    height: auto;
    min-height: 4.5em;
    padding: 10px 16px;
    margin-top: 0.5em;
    margin-left: 16px;
    margin-right: 16px;
    box-shadow: 4px 4px 0px #000;
    border-radius: 25px;
    justify-content: space-between;
  }

  .studentlogo-image {
    height: 40px !important;
  }

  .logo-section {
    flex: 0 0 auto;
  }

  .navigation {
    display: none;
  }

  .action-buttons {
    display: none; /* Hide action buttons on tablet, use hamburger menu instead */
  }

  .mobile-menu-container {
    display: block;
    margin-left: auto;
  }
}

@media (max-width: 600px) {
  .header-container {
    width: calc(100% - 24px);
    max-width: calc(100vw - 24px);
    height: auto;
    min-height: 4em;
    padding: 8px 14px;
    margin-top: 0.4em;
    margin-left: 12px;
    margin-right: 12px;
    box-shadow: 3px 3px 0px #000;
    border-radius: 20px;
  }

  .studentlogo-image {
    height: 35px !important;
  }
}

@media (max-width: 480px) {
  .header-container {
    width: calc(100% - 16px);
    max-width: calc(100vw - 16px);
    height: auto;
    min-height: 3.5em;
    padding: 8px 12px;
    margin-top: 0.3em;
    margin-left: 8px;
    margin-right: 8px;
    box-shadow: 3px 3px 0px #000;
    border-radius: 18px;
    justify-content: space-between;
    position: relative;
  }

  .studentlogo-image {
    height: 32px !important;
  }

  .navigation {
    display: none;
  }

  .action-buttons {
    display: none; /* Hide action buttons on mobile, use hamburger menu instead */
  }

  .mobile-menu-container {
    display: block;
  }
}

.header .nav-link,
.header .nav-link:hover,
.header .nav-link:active,
.header .nav-link.active {
  color: #ffffff !important;
}

.header .nav-link.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  color: #9ca3af !important;
}

.header .mobile-nav-link,
.header .mobile-nav-link:hover,
.header .mobile-nav-link.router-link-active {
  color: #ffffff !important;
}

.header .logo-text {
  color: #ffffff !important;
}

/* Unread Messages Badge Styles */
.messages-link {
  position: relative;
}

.unread-badge {
  position: absolute;
  top: -8px;
  right: -12px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  min-width: 20px;
  padding: 0 2px;
}

.unread-badge.mobile {
  top: -6px;
  right: -10px;
  width: 18px;
  height: 18px;
  font-size: 10px;
  min-width: 18px;
}

/* Handle larger numbers */
.unread-badge:has-text {
  border-radius: 10px;
  padding: 0 4px;
  min-width: 20px;
  width: auto;
}

.unread-badge.mobile:has-text {
  border-radius: 9px;
  padding: 0 3px;
  min-width: 18px;
  width: auto;
}

/* Animation for new messages */
@keyframes badge-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.unread-badge {
  animation: badge-pulse 0.3s ease-in-out;
}
</style>
